/** @file A plan's `created` map: one key per entity, `<kind>:<name>` where kinds share a name. */
import { ids } from '@pose2d/format/ids';
import type { Id } from '@pose2d/format/types';
import { KIND_PREFIX } from '../_shared/kindPrefix';

/** Id prefix -> entity kind (`att` -> `attachment`). */
const KIND_OF: ReadonlyMap<string, string> = new Map(Object.entries(KIND_PREFIX).map(([kind, prefix]) => [prefix, kind]));

/**
 * Folds the (name, id) pairs the plan commands created, in order: a name only one kind created keeps its plain key;
 * a name several kinds created (bone, slot and attachment `tail`) gets one `<kind>:<name>` key per kind, so no id is
 * lost (m39). Names never contain `:`. A later entity of the same kind and name replaces the earlier one.
 */
export function createdByKind(entries: readonly (readonly [string, Id])[]): Record<string, Id> {
  const byName = new Map<string, Map<string, Id>>();
  for (const [name, id] of entries) {
    const kinds = byName.get(name) ?? new Map<string, Id>();
    kinds.set(KIND_OF.get(ids.prefixOf(id) ?? '') ?? 'entity', id);
    byName.set(name, kinds);
  }
  const out: Record<string, Id> = {};
  for (const [name, kinds] of byName) {
    for (const [kind, id] of kinds) out[kinds.size === 1 ? name : `${kind}:${name}`] = id;
  }
  return out;
}
